import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/ui-bits";
import { usePageMeta } from "@/hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SHIPMENT_TYPES = [
  "FTL & Partial Loads",
  "Dedicated Transportation",
  "Expedited & Hot Shot",
  "Specialized & White-Glove Delivery",
  "3PL & Freight Brokerage",
  "Not Sure — Help Me Decide",
];

const DELIVERY_REQUIREMENTS = [
  "Tailgate Delivery",
  "Two-Person Delivery",
  "Inside Delivery",
  "Residential Delivery",
  "Appointment Delivery",
  "Unpacking / Uncrating",
  "Debris Removal",
];

const inputClass =
  "w-full border border-[#e5e7eb] bg-white px-4 py-3 text-sm text-[#191c1e] placeholder:text-[#9aa1a8] transition-colors duration-200 focus:border-[#ff6a00] focus:outline-none";

const labelClass = "mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-[#191c1e]/70";

const Field = ({ label, required, children, testid }) => (
  <div data-testid={testid}>
    <label className="block">
      <span className={labelClass}>
        {label} {required && <span className="text-[#ff6a00]">*</span>}
      </span>
      {children}
    </label>
  </div>
);

const INITIAL = {
  first_name: "",
  last_name: "",
  company_name: "",
  email: "",
  phone: "",
  pickup_location: "",
  delivery_location: "",
  shipment_type: "",
  pickup_date: "",
  delivery_date: "",
  delivery_requirements: [],
  additional_details: "",
  website: "",
};

export default function RequestQuotePage() {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const startedAt = useRef(Date.now());

  usePageMeta({
    title: "Request a Freight Quote | 30 NORTH",
    description:
      "Request a transportation quote from 30 NORTH. Tell us about your shipment and requirements so we can determine the right transportation solution.",
    path: "/request-a-quote",
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleRequirement = (req) =>
    setForm((f) => ({
      ...f,
      delivery_requirements: f.delivery_requirements.includes(req)
        ? f.delivery_requirements.filter((r) => r !== req)
        : [...f.delivery_requirements, req],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting" || status === "success") return;
    setStatus("submitting");
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/quote-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, form_started_at: startedAt.current }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          res.status === 422
            ? "Please review the highlighted fields and try again."
            : data.detail || "Your request could not be sent. Please try again."
        );
      }
      const data = await res.json();
      setConfirmation(data.customer_confirmation || "sent");
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setStatus("error");
      setError(err.message || "Your request could not be sent. Please try again.");
    }
  };

  return (
    <div data-testid="request-quote-page">
      <section className="bg-white py-16 sm:py-20 lg:py-24" aria-labelledby="quote-heading">
        <div className="container-30 grid gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <Eyebrow>Request a Quote</Eyebrow>
            <h1
              id="quote-heading"
              data-testid="quote-headline"
              className="mt-6 text-4xl font-black leading-[1.02] tracking-tight text-[#191c1e] sm:text-5xl"
            >
              TELL US WHAT YOU'RE MOVING.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#5f666d] sm:text-lg">
              Tell us what you're moving, where it's going and when it needs to arrive. We'll help determine the right
              transportation solution.
            </p>

            {status === "success" ? (
              <div className="mt-12 border border-[#e5e7eb] bg-[#f7f8f9] p-8 lg:p-12" data-testid="quote-success">
                <CheckCircle2 className="h-10 w-10 text-[#ff6a00]" strokeWidth={1.5} aria-hidden="true" />
                <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-[#191c1e]">
                  REQUEST RECEIVED.
                </h2>
                <p className="mt-4 max-w-md text-base leading-relaxed text-[#5f666d]">
                  Thank you, {form.first_name}. Your transportation quote request has been sent to our operations team
                  at 30 NORTH.
                </p>
                {confirmation === "sent" ? (
                  <p className="mt-4 text-sm font-semibold text-[#191c1e]" data-testid="quote-confirmation-note">
                    A confirmation email has been sent to {form.email}.
                  </p>
                ) : confirmation === "failed" ? (
                  <p className="mt-4 max-w-md border-l-2 border-[#ff6a00] pl-4 text-sm leading-relaxed text-[#5f666d]" data-testid="quote-confirmation-note">
                    Your request was received, but we couldn't send a confirmation email to {form.email}. We'll still
                    be in touch using the contact details you provided.
                  </p>
                ) : null}
                <p className="mt-4 text-base leading-relaxed text-[#5f666d]">
                  Need to reach us directly? Call{" "}
                  <a href="tel:6138700294" data-testid="quote-success-phone" className="font-semibold text-[#191c1e] underline decoration-[#ff6a00] decoration-2 underline-offset-4">
                    613-870-0294
                  </a>
                  .
                </p>
                <div className="mt-10">
                  <Link
                    to="/"
                    data-testid="quote-success-home"
                    className="inline-flex items-center gap-2 border border-[#191c1e]/25 px-7 py-4 text-[13px] font-bold tracking-[0.12em] text-[#191c1e] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#191c1e]"
                  >
                    BACK TO HOME
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="quote-form" className="mt-12" noValidate={false}>
                <fieldset>
                  <legend className="text-xs font-bold uppercase tracking-[0.22em] text-[#ff6a00]">
                    Contact Information
                  </legend>
                  <div className="mt-6 grid gap-5 sm:grid-cols-2">
                    <Field label="First Name" required testid="field-first-name">
                      <input required data-testid="input-first-name" className={inputClass} value={form.first_name} onChange={set("first_name")} autoComplete="given-name" />
                    </Field>
                    <Field label="Last Name" required testid="field-last-name">
                      <input required data-testid="input-last-name" className={inputClass} value={form.last_name} onChange={set("last_name")} autoComplete="family-name" />
                    </Field>
                    <Field label="Company Name" testid="field-company-name">
                      <input data-testid="input-company-name" className={inputClass} value={form.company_name} onChange={set("company_name")} autoComplete="organization" />
                    </Field>
                    <Field label="Phone" required testid="field-phone">
                      <input required type="tel" data-testid="input-phone" className={inputClass} value={form.phone} onChange={set("phone")} autoComplete="tel" />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Email" required testid="field-email">
                        <input required type="email" data-testid="input-email" className={inputClass} value={form.email} onChange={set("email")} autoComplete="email" />
                      </Field>
                    </div>
                  </div>
                </fieldset>

                <fieldset className="mt-12">
                  <legend className="text-xs font-bold uppercase tracking-[0.22em] text-[#ff6a00]">
                    Shipment Information
                  </legend>
                  <div className="mt-6 grid gap-5 sm:grid-cols-2">
                    <Field label="Pickup Location" required testid="field-pickup-location">
                      <input required data-testid="input-pickup-location" className={inputClass} placeholder="City, Province" value={form.pickup_location} onChange={set("pickup_location")} />
                    </Field>
                    <Field label="Delivery Location" required testid="field-delivery-location">
                      <input required data-testid="input-delivery-location" className={inputClass} placeholder="City, Province" value={form.delivery_location} onChange={set("delivery_location")} />
                    </Field>
                    <Field label="Shipment Type" required testid="field-shipment-type">
                      <select required data-testid="input-shipment-type" className={inputClass} value={form.shipment_type} onChange={set("shipment_type")}>
                        <option value="" disabled>
                          Select a shipment type
                        </option>
                        {SHIPMENT_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Pickup Date" testid="field-pickup-date">
                      <input type="date" data-testid="input-pickup-date" className={inputClass} value={form.pickup_date} onChange={set("pickup_date")} />
                    </Field>
                    <Field label="Delivery Date / Required Date" testid="field-delivery-date">
                      <input type="date" data-testid="input-delivery-date" className={inputClass} value={form.delivery_date} onChange={set("delivery_date")} />
                    </Field>
                    <div className="sm:col-span-2" data-testid="field-delivery-requirements">
                      <span className={labelClass}>Delivery Requirements</span>
                      <div className="flex flex-wrap gap-2">
                        {DELIVERY_REQUIREMENTS.map((req) => {
                          const active = form.delivery_requirements.includes(req);
                          return (
                            <button
                              type="button"
                              key={req}
                              data-testid={`requirement-${req.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                              aria-pressed={active}
                              onClick={() => toggleRequirement(req)}
                              className={`border px-4 py-2.5 text-[12px] font-bold tracking-[0.08em] transition-colors duration-200 ${
                                active
                                  ? "border-[#ff6a00] bg-[#ff6a00] text-white"
                                  : "border-[#191c1e]/20 bg-white text-[#191c1e] hover:border-[#ff6a00]"
                              }`}
                            >
                              {req.toUpperCase()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Additional Details / Special Requirements" testid="field-additional-details">
                        <textarea
                          rows={4}
                          data-testid="input-additional-details"
                          className={`${inputClass} resize-y`}
                          placeholder="Freight type, pallet count, access notes, timing considerations…"
                          value={form.additional_details}
                          onChange={set("additional_details")}
                        />
                      </Field>
                    </div>
                  </div>
                </fieldset>

                {status === "error" && (
                  <div
                    role="alert"
                    data-testid="quote-error"
                    className="mt-8 flex items-start gap-3 border border-[#191c1e] bg-white p-4"
                  >
                    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#ff6a00]" aria-hidden="true" />
                    <div>
                      <p className="text-sm font-bold text-[#191c1e]">Your request was not sent.</p>
                      <p className="mt-1 text-sm text-[#5f666d]">{error} You can retry below — your entries are kept.</p>
                    </div>
                  </div>
                )}

                <div aria-hidden="true" className="absolute -left-[9999px] -top-[9999px] h-0 overflow-hidden">
                  <label>
                    Website
                    <input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      data-testid="input-website-honeypot"
                      value={form.website}
                      onChange={set("website")}
                    />
                  </label>
                </div>

                <p className="mt-8 text-[13px] leading-relaxed text-[#5f666d]" data-testid="quote-consent">
                  By submitting this form, I agree to be contacted by 30 NORTH regarding this quote request. Review
                  our{" "}
                  <Link to="/privacy-policy" data-testid="quote-consent-privacy" className="font-semibold text-[#191c1e] underline decoration-[#ff6a00] decoration-2 underline-offset-4 hover:text-[#ff6a00]">
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms-and-conditions" data-testid="quote-consent-terms" className="font-semibold text-[#191c1e] underline decoration-[#ff6a00] decoration-2 underline-offset-4 hover:text-[#ff6a00]">
                    Terms &amp; Conditions
                  </Link>
                  .
                </p>

                <div className="mt-6">
                  <button
                    type="submit"
                    data-testid="quote-submit-button"
                    disabled={status === "submitting"}
                    className="group inline-flex w-full items-center justify-center gap-2 whitespace-nowrap bg-[#ff6a00] px-7 py-4 text-[13px] font-bold tracking-[0.12em] text-white shadow-[0_10px_24px_-10px_rgba(255,106,0,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#e05d00] hover:shadow-[0_16px_32px_-10px_rgba(255,106,0,0.65)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 sm:w-auto"
                  >
                    {status === "submitting" ? "SENDING…" : "SUBMIT REQUEST"}
                    {status !== "submitting" && (
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-5">
            <aside className="border border-[#2b2f33] bg-[#191c1e] p-8 text-white lg:sticky lg:top-28 lg:p-10" data-testid="quote-contact-card">
              <h2 className="text-xs font-bold uppercase tracking-[0.22em] text-white/40">Contact 30 NORTH</h2>
              <a href="tel:6138700294" data-testid="quote-phone-link" className="mt-6 flex items-center gap-4 text-white transition-colors duration-200 hover:text-[#ff6a00]">
                <Phone className="h-5 w-5 shrink-0 text-[#ff6a00]" aria-hidden="true" />
                <span className="font-display text-xl font-bold tracking-wide">613-870-0294</span>
              </a>
              <a href="mailto:operations@30north.ca" data-testid="quote-email-link" className="mt-5 flex items-center gap-4 text-white/80 transition-colors duration-200 hover:text-[#ff6a00]">
                <Mail className="h-5 w-5 shrink-0 text-[#ff6a00]" aria-hidden="true" />
                <span className="break-all text-base font-medium">operations@30north.ca</span>
              </a>
              <p className="mt-6 flex items-start gap-4 text-sm text-white/60">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#ff6a00]" aria-hidden="true" />
                <span data-testid="quote-address">
                  2907 - 80 Absolute Ave
                  <br />
                  Mississauga, ON L4Z 0A5
                  <br />
                  Canada
                </span>
              </p>
              <div className="mt-8 border-t border-[#2b2f33] pt-6">
                <h3 className="text-xs font-bold uppercase tracking-[0.22em] text-white/40">What Happens Next</h3>
                <ul className="mt-4 space-y-3">
                  {[
                    "We review your shipment details",
                    "We determine the right transportation solution",
                    "We follow up with your quote",
                  ].map((step, i) => (
                    <li key={step} className="flex items-center gap-3" data-testid={`quote-step-${i + 1}`}>
                      <span className="block h-1.5 w-1.5 shrink-0 bg-[#ff6a00]" aria-hidden="true" />
                      <span className="text-sm text-white/70">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
